import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { FeatureFlag } from '@core/enums';
import { AccountState } from '@state/account/state';
import { Note } from '@shared/modules/notes/notes.types';
import { NotesState } from '@shared/modules/notes/state/state';
import { MentionsService } from '@shared/services/mentions.service';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';
import { PanelsInterfaceComponent, PanelsInterfaceComponentBase } from '../panels-interface.component';

@Component({
  selector: 'project-panel-notes',
  templateUrl: './notes.component.html',
  styleUrls: ['./notes.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsNotesComponent extends PanelsInterfaceComponentBase implements PanelsInterfaceComponent, OnInit {
  @Select(NotesState.getNotesList) notes$: Observable<Note[]>;

  notes: Note[];
  displayColumns: string[] = ['title', 'body', 'created'];
  columnDefs: ColumnDef[] = [];
  newTable: boolean;
  actions: TableActions[] = [];

  readonly mentionsService = inject(MentionsService);

  constructor(
    protected readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly store: Store,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    super(changeDetectorRef);
  }

  ngOnInit() {
    this.checkTable();

    if (this.project && this.project.notes) {
      this.notes = this.mapToMentions(this.project.notes);
    } else {
      this.store
        .select(NotesState.getNotesList)
        .pipe(untilComponentDestroyed(this))
        .subscribe((notes: Note[]) => {
          this.notes = this.mapToMentions(notes);
          this.changeDetectorRef.markForCheck();
        });
    }

    this.titleFieldDef = {
      name: 'notes.panel_name',
      label: this.panelDef.title,
      type: 'text',
    };

    if (this.newTable) {
      this.setColDefs();
    }
  }

  private mapToMentions(notes: Note[]): Note[] {
    return notes.map((note) => ({
      ...note,
      body: this.mentionsService.formatMessage(note.body),
    }));
  }

  private setColDefs(): void {
    this.columnDefs = [
      {
        label: 'Title',
        name: 'title',
        type: 'text',
        tsTableOptions: {
          headerName: 'Title',
          field: 'title',
          editable: false,
          flex: 1,
        },
      },
      {
        label: 'Body',
        name: 'body',
        type: 'text',
        tsTableOptions: {
          headerName: 'Body',
          field: 'body',
          editable: false,
          flex: 2,
          cellRenderer: (params) => {
            return `<div class="truncate">${params.value ? params.value : ''}</div>`;
          },
        },
      },
      {
        label: 'Created',
        name: 'created',
        type: 'date',
        tsTableOptions: {
          headerName: 'Created',
          field: 'created_at',
          editable: false,
          flex: 1,
        },
      },
    ];

    this.setActionsColumn();
  }

  private checkTable(): void {
    this.newTable = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_TABLE));
  }

  private setActionsColumn(): void {
    this.actions = [
      {
        label: 'Open Note',
        icon: 'visibility',
        action: (note: Note) => this.goToNote(note),
      },
    ];
  }

  goToNote(note: Note): void {
    if (note && note.id) {
      const currentUrl = this.router.url;
      if (currentUrl.includes('tabs')) {
        const baseUrl = currentUrl.split('/details/tabs')[0];
        const newUrl = `${baseUrl}/notes/${note.id}`;
        this.router.navigateByUrl(newUrl);
      } else {
        this.router.navigate(['../notes', note.id], { relativeTo: this.route });
      }
    }
  }

  goToNotes(): void {
    const currentUrl = this.router.url;
    if (currentUrl.includes('tabs')) {
      const baseUrl = currentUrl.split('/details/tabs')[0];
      const newUrl = `${baseUrl}/notes`;

      this.router.navigateByUrl(newUrl);
    } else {
      this.router.navigate(['../notes'], { relativeTo: this.route });
    }
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
